import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiTrash2, FiEdit3, FiSave, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { subjects } from '../data/flashcards';
import './Settings.css';

const Settings = ({ flashcards, setFlashcards }) => {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [newCard, setNewCard] = useState({ question: '', answer: '', image: '', category: 'organs' });
  const [expandedCategories, setExpandedCategories] = useState({
    organs: true,
    cars: false,
    animals: false,
    food: false,
    custom: true
  });

  // Group flashcards by category
  const groupedFlashcards = {
    organs: flashcards.filter(card => card.id >= 1 && card.id <= 10),
    cars: flashcards.filter(card => card.id >= 11 && card.id <= 20),
    animals: flashcards.filter(card => card.id >= 21 && card.id <= 30),
    food: flashcards.filter(card => card.id >= 31 && card.id <= 40),
    custom: flashcards.filter(card => card.id > 40)
  };

  const handleAddCard = () => {
    if (newCard.question.trim() && newCard.answer.trim()) {
      const card = {
        id: Date.now(),
        question: newCard.question.trim(),
        answer: newCard.answer.trim(),
        image: newCard.image.trim() || null,
        category: newCard.category
      };
      setFlashcards([...flashcards, card]);
      setNewCard({ question: '', answer: '', image: '', category: 'organs' });
      setIsAddingCard(false);
      // Expand the custom category to show the new card
      setExpandedCategories(prev => ({ ...prev, custom: true }));
    }
  };

  const handleEditCard = (card) => {
    setEditingCard({ ...card });
  };

  const handleSaveEdit = () => {
    if (editingCard.question.trim() && editingCard.answer.trim()) {
      const updatedCards = flashcards.map(card =>
        card.id === editingCard.id ? editingCard : card
      );
      setFlashcards(updatedCards);
      setEditingCard(null);
    }
  };

  const handleDeleteCard = (cardId) => {
    const updatedCards = flashcards.filter(card => card.id !== cardId);
    setFlashcards(updatedCards);
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryInfo = (categoryKey) => {
    if (categoryKey === 'custom') {
      return { name: 'My Custom Cards', icon: '✨', color: '#9C27B0' };
    }
    return subjects.find(s => s.key === categoryKey) || { name: 'Unknown', icon: '❓', color: '#666' };
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  const renderFlashcardItem = (card, index, category) => (
    <motion.div
      key={card.id}
      className="flashcard-item"
      variants={itemVariants}
      whileHover={{ scale: 1.01 }}
      layout
    >
      {editingCard && editingCard.id === card.id ? (
        <div className="card-form">
          <div className="form-group">
            <label>Question</label>
            <textarea
              value={editingCard.question}
              onChange={(e) => setEditingCard({ ...editingCard, question: e.target.value })}
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Answer</label>
            <textarea
              value={editingCard.answer}
              onChange={(e) => setEditingCard({ ...editingCard, answer: e.target.value })}
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Image URL (optional)</label>
            <input
              type="url"
              value={editingCard.image || ''}
              onChange={(e) => setEditingCard({ ...editingCard, image: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSaveEdit}>
              <FiSave />
              <span>Save</span>
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => setEditingCard(null)}
            >
              <FiX />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="card-content">
            <div className="card-number">{index + 1}</div>
            <div className="card-text">
              <div className="question">
                <strong>Q:</strong> {card.question}
              </div>
              <div className="answer">
                <strong>A:</strong> {card.answer}
              </div>
              {card.image && (
                <div className="card-image-preview">
                  <img src={card.image} alt="Card" />
                </div>
              )}
            </div>
          </div>
          <div className="card-actions">
            <motion.button
              className="action-btn edit-btn"
              onClick={() => handleEditCard(card)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title="Edit card"
            >
              <FiEdit3 />
            </motion.button>
            {/* Only allow deleting custom cards */}
            {card.id > 40 && (
              <motion.button
                className="action-btn delete-btn"
                onClick={() => handleDeleteCard(card.id)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                title="Delete card"
              >
                <FiTrash2 />
              </motion.button>
            )}
          </div>
        </>
      )}
    </motion.div>
  );

  return (
    <motion.div 
      className="settings-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="settings-header" variants={itemVariants}>
        <h1>Settings</h1>
        <p>Manage your flashcards and customize your learning experience.</p>
      </motion.div>

      <motion.div className="flashcards-section" variants={itemVariants}>
        <div className="section-header">
          <h2>Flashcard Library ({flashcards.length} total)</h2>
          <motion.button
            className="btn btn-primary add-card-btn"
            onClick={() => setIsAddingCard(true)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiPlus />
            <span>Add Custom Card</span>
          </motion.button>
        </div>

        {isAddingCard && (
          <motion.div 
            className="card-form add-card-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <h3>Add New Custom Card</h3>
            <div className="form-group">
              <label>Category</label>
              <select
                value={newCard.category}
                onChange={(e) => setNewCard({ ...newCard, category: e.target.value })}
              >
                {subjects.map(subject => (
                  <option key={subject.key} value={subject.key}>
                    {subject.icon} {subject.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Question</label>
              <textarea
                value={newCard.question}
                onChange={(e) => setNewCard({ ...newCard, question: e.target.value })}
                placeholder="What is this called? or What animal is this?"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Answer</label>
              <textarea
                value={newCard.answer}
                onChange={(e) => setNewCard({ ...newCard, answer: e.target.value })}
                placeholder="Enter the answer..."
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Image URL (optional)</label>
              <input
                type="url"
                value={newCard.image}
                onChange={(e) => setNewCard({ ...newCard, image: e.target.value })}
                placeholder="https://images.unsplash.com/photo-xyz..."
              />
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleAddCard}>
                <FiSave />
                <span>Save Card</span>
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setIsAddingCard(false);
                  setNewCard({ question: '', answer: '', image: '', category: 'organs' });
                }}
              >
                <FiX />
                <span>Cancel</span>
              </button>
            </div>
          </motion.div>
        )}

        {/* Render each category */}
        {Object.entries(groupedFlashcards).map(([categoryKey, cards]) => {
          if (cards.length === 0 && categoryKey !== 'custom') return null;
          
          const categoryInfo = getCategoryInfo(categoryKey);
          const isExpanded = expandedCategories[categoryKey];
          
          return (
            <motion.div key={categoryKey} className="category-section" variants={itemVariants}>
              <div 
                className="category-header"
                onClick={() => toggleCategory(categoryKey)}
                style={{ '--category-color': categoryInfo.color }}
              >
                <div className="category-info">
                  <span className="category-icon">{categoryInfo.icon}</span>
                  <span className="category-name">{categoryInfo.name}</span>
                  <span className="category-count">({cards.length} cards)</span>
                </div>
                <div className="category-toggle">
                  {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </div>
              </div>
              
              {isExpanded && (
                <motion.div 
                  className="flashcards-list"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {cards.length > 0 ? (
                    cards.map((card, index) => renderFlashcardItem(card, index, categoryKey))
                  ) : (
                    <div className="empty-category">
                      <p>No custom cards in this category yet.</p>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => {
                          setNewCard({ ...newCard, category: categoryKey });
                          setIsAddingCard(true);
                        }}
                      >
                        <FiPlus /> Add First Card
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </motion.div>
    </motion.div>
  );
};

export default Settings; 